import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldOff } from 'lucide-react';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const MIN_REASON = 10;
const BLOCKED_QC_STATES = ['pending_review', 'pending', 'escalated', 'returned', 'rejected', 'cancelled', 'resubmission_required'];

export const canOverrideQc = (role) => (role || '').toLowerCase() === 'admin';

// QC-RULE-001 — ADMIN-ONLY emergency override. Exceptional: an order that entered QC
// normally must be resubmitted and approved by QC. Mandatory reason; server records
// qc_status=bypassed / admin_emergency_override + history + activity. NOT a QC approval.
// QC-OVERRIDE-UI-001: this confirmation is opened from INSIDE the Correction Required
// modal (z-index 60/61), so it must stack above it — otherwise Radix parks focus and
// pointer-events in an invisible dialog and the whole screen looks frozen.
export default function QcOverrideCompleteButton({ orderId, orderNumber, workflow, userRole, orderStatus, qcStatus, qcIteration, onCompleted, className = '' }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const blocked = orderStatus === 'qc_review' || BLOCKED_QC_STATES.includes(qcStatus);
  if (!blocked || ['complete', 'completed', 'cancelled', 'typing'].includes(orderStatus) || !canOverrideQc(userRole)) return null;

  const reasonOk = reason.trim().length >= MIN_REASON;

  const submit = async () => {
    if (inFlight.current || !reasonOk) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const body = { qc_override_reason: reason.trim() };
      const url = workflow === 'tax'
        ? `${API}/api/orders/${orderId}/complete`
        : `${API}/api/orders/${orderId}/search-complete`;
      const res = await axios.post(url, body, { timeout: 60000 });
      const st = res.data?.status;
      if (workflow === 'title' && ['validation_failed', 'error', 'qc_required'].includes(st)) {
        toast.error((res.data.errors || ['Unable to complete this order'])[0]);
        return;
      }
      toast.success(`${orderNumber || 'Order'} completed via Admin Emergency Override (recorded as QC bypassed — not a QC approval)`);
      setOpen(false);
      const patch = workflow === 'tax'
        ? { status: 'complete', qc_status: 'bypassed', is_locked: true }
        : { status: st === 'routed_to_typing' ? 'typing' : 'completed', qc_status: 'bypassed' };
      onCompleted?.(patch, res.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.message || (e?.code === 'ECONNABORTED' ? 'Override timed out — the order was not changed. Please try again.' : 'Override failed — the order was not changed.'));
      toast.error(msg);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className={`border-orange-300 text-orange-800 hover:bg-orange-50 ${className}`}
        onClick={() => { setReason(''); setOpen(true); }}
        data-testid="qc-override-complete-btn"
      >
        <ShieldOff className="w-3.5 h-3.5 mr-1" /> Admin Emergency Override — Complete Without QC
      </Button>
      <AlertDialog open={open} onOpenChange={(o) => { if (busy) return; setOpen(o); }}>
        <AlertDialogContent
          data-testid="qc-override-dialog"
          className="z-[81] max-w-xl"
          overlayClassName="z-[80]"
          onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="qc-override-title">Complete this order without QC?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-left">
                <p data-testid="qc-override-description">
                  This will bypass the outstanding QC correction requirement{qcIteration ? ` (QC iteration ${qcIteration})` : ''} and complete
                  {' '}<strong>{orderNumber || 'this order'}</strong> using the Admin Emergency Override. The QC cycle is recorded as
                  <strong> bypassed — this is not a QC approval</strong>. Who performed the override, the date/time, your reason, and the
                  previous/resulting status are written to the QC history and the Order Activity log.
                </p>
                <div>
                  <label htmlFor="qc-override-reason" className="block text-xs font-semibold text-slate-700 mb-1">
                    Override Reason <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    id="qc-override-reason"
                    data-testid="qc-override-reason"
                    className="w-full border rounded-md p-2 text-sm min-h-[80px] disabled:opacity-60"
                    placeholder={`Emergency justification (minimum ${MIN_REASON} characters, required)`}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={500}
                    disabled={busy}
                    autoFocus
                  />
                  <p className="text-xs text-slate-500" data-testid="qc-override-reason-count">{reason.trim().length}/{MIN_REASON} minimum</p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="qc-override-cancel" disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="qc-override-confirm"
              disabled={busy || !reasonOk}
              onClick={(e) => { e.preventDefault(); submit(); }}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {busy ? 'Completing…' : 'Confirm Emergency Override'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
